import type {
  IRouter,
  IRouterPage,
} from '@wix/public-editor-platform-interfaces';
import type {
  CompRef,
  RouterRef,
  RoutersDefinitionWithId,
} from '@wix/document-services-types';
import type { PlatformPrivateServices } from '../../PlatformPrivateServices';
import { RouterPageHandle } from '../Pages';
import type { EditorPointer } from '@wix/editor-document-logic-api';

const _routerKeys = new WeakMap<IRouter, string>();

export class RouterHandle implements IRouter {
  #ps: PlatformPrivateServices;
  #editorPointer: EditorPointer<RouterRef>;
  #shims = new Set<IRouter>();

  constructor(
    ps: PlatformPrivateServices,
    editorPointer: EditorPointer<RouterRef>,
  ) {
    this.#ps = ps;
    this.#editorPointer = editorPointer;
    _routerKeys.set(this, editorPointer.asUniqueKey);
  }

  #getRouterRef(): RouterRef {
    const ref =
      this.#ps.platformPackagesBridge.EditorPointerAPI?.getDocumentPointer(
        this.#editorPointer,
      );
    if (!ref) {
      throw new Error('EditorPointerAPI is not available');
    }
    return ref;
  }

  #getRouter(): RoutersDefinitionWithId {
    const router = this.#ps.ds.routers.get.byRef(this.#getRouterRef());
    if (!router) {
      throw new Error('Router not found');
    }
    return router;
  }

  #isConnectablePage(pageRefId: string, appDefId: string): boolean {
    const { ds } = this.#ps;
    if (!ds.routers.pages.isConnectablePage(pageRefId)) {
      return false;
    }
    const pageData = ds.pages.data.get(pageRefId);
    const effectiveAppDefId =
      pageData?.managingAppDefId ?? pageData?.appDefinitionId;
    return !effectiveAppDefId || effectiveAppDefId === appDefId;
  }

  #buildRouterPage(pageRef: CompRef): IRouterPage {
    const editorPointerAPI = this.#ps.platformPackagesBridge.EditorPointerAPI;
    if (!editorPointerAPI) {
      throw new Error('EditorPointerAPI is not available');
    }
    const { innerPath } = pageRef;
    const innerPathArr = Array.isArray(innerPath)
      ? innerPath
      : innerPath
      ? [innerPath]
      : [];
    const pageEditorPointer = editorPointerAPI.buildEditorPointer(
      pageRef,
      innerPathArr,
    );
    return new RouterPageHandle(
      this.#ps,
      pageEditorPointer,
      this.#editorPointer,
    ).asIRouterPage();
  }

  async getExtensionId(): Promise<string | undefined> {
    return this.#getRouter().extensionId;
  }

  async getPrefix(): Promise<string> {
    return this.#getRouter().prefix;
  }

  async getConfig(): Promise<object> {
    return this.#getRouter().config;
  }

  async listConnectablePages(): Promise<IRouterPage[]> {
    const { appDefinitionId } = this.#getRouter();
    const { ds } = this.#ps;
    return ds.pages
      .getPageIdList(false, false)
      .filter((pageRefId) =>
        this.#isConnectablePage(pageRefId, appDefinitionId),
      )
      .map((pageRefId) =>
        this.#buildRouterPage(ds.pages.getReference(pageRefId)),
      );
  }

  async listConnectedPages(): Promise<IRouterPage[]> {
    const { ds } = this.#ps;
    return Object.values(this.#getRouter().pages ?? {}).map((pageRefId) =>
      this.#buildRouterPage(ds.pages.getReference(pageRefId)),
    );
  }

  async updatePrefix(prefix: string): Promise<void> {
    this.#getRouter();
    this.#ps.ds.routers.update(this.#getRouterRef(), { prefix });
    await this.#ps.ds.waitForChangesAppliedAsync();
  }

  async updateConfig(config: object): Promise<void> {
    this.#getRouter();
    this.#ps.ds.routers.update(this.#getRouterRef(), {
      config: JSON.stringify(config),
    });
    await this.#ps.ds.waitForChangesAppliedAsync();
  }

  async remove(): Promise<void> {
    this.#getRouter();
    this.#ps.ds.routers.remove(this.#getRouterRef());
    await this.#ps.ds.waitForChangesAppliedAsync();
    _routerKeys.delete(this);
    this.#shims.forEach((shim) => _routerKeys.delete(shim));
    this.#shims.clear();
  }

  equals(other: IRouter): boolean {
    const routerKey = _routerKeys.get(this);
    return routerKey !== undefined && routerKey === _routerKeys.get(other);
  }

  asIRouter(): IRouter {
    const iRouter: IRouter = {
      getExtensionId: () => this.getExtensionId(),
      getPrefix: () => this.getPrefix(),
      getConfig: () => this.getConfig(),
      listConnectablePages: () => this.listConnectablePages(),
      listConnectedPages: () => this.listConnectedPages(),
      updatePrefix: (prefix: string) => this.updatePrefix(prefix),
      updateConfig: (config: object) => this.updateConfig(config),
      remove: () => this.remove(),
      equals: (other: IRouter) => this.equals(other),
    };
    _routerKeys.set(iRouter, this.#editorPointer.asUniqueKey);
    this.#shims.add(iRouter);
    return iRouter;
  }
}
